import { useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Loader2, Wifi } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useCan } from '@/hooks/useCan';
import { refreshZoneFeatures } from '@/hooks/useZoneFeatures';
import { extractApiError } from '@/utils/apiError';
import { getWifiNetworks, getWifiSettings, setWifiEnabled, setWifiNetworks, type WifiNetworkDto, type WifiSettingsDto } from '@/api/wifi';
import { getCaptiveSettings, putCaptivePortal, type CaptiveSettingsDto } from '@/api/captive';
import { CaptivePortalEditor } from '@/components/captive/CaptivePortalEditor';

/** «Оборудование → роутер → собирать статистику» (W2, Alexey 16.09): the guest-Wi-Fi
 *  function has no settings tab of its own — this switch is the whole setup, and the
 *  numbers show up in Статистика. Turning it on probes the router straight away so a
 *  wrong password is visible here, not silently five minutes later. */
export function RouterWifiStats({ zoneId }: { zoneId: number | string }) {
  const canEdit = useCan('zone.config.edit', zoneId);
  const key = `zone-wifi-settings:${zoneId}`;
  const { data, mutate } = useSWR<WifiSettingsDto>(key, () => getWifiSettings(zoneId).then(r => r.data), {
    revalidateOnFocus: false, shouldRetryOnError: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<string | null>(null);

  if (!data) return null;

  async function toggle(next: boolean) {
    setBusy(true); setError(null); setProbe(null);
    try {
      const resp = await setWifiEnabled(zoneId, next);
      if (resp.data.probe) {
        setProbe(resp.data.probe.error
          ? `Не получилось опросить роутер: ${resp.data.probe.error}`
          : `Роутер ответил: сейчас онлайн ${resp.data.probe.clients} устройств`);
      }
      await mutate();
      refreshZoneFeatures(String(zoneId));
    } catch (e) {
      setError(extractApiError(e, 'Не удалось переключить'));
    } finally {
      setBusy(false);
    }
  }

  const ready = data.router_ready && (data.router_model || '').toLowerCase() === 'keenetic';
  return (
    <section className="space-y-2 border-t border-border pt-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-medium"><Wifi className="size-4" /> Статистика Wi-Fi</div>
          {/* Прежний текст обещал меньше, чем железо умеет, и владелец на это
              указал (AI106): объём трафика по устройству роутер отдаёт в том же
              ответе, а «сайты» он не хранит вовсе — ни истории, ни адресов. */}
          <p className="mt-0.5 text-xs text-muted-foreground">
            Раз в 5 минут спрашиваем роутер, какие устройства в сети: сколько гостей, кто заходит повторно, сколько
            сидят, телефоны или ноутбуки, сколько трафика. Адресов сайтов у роутера нет — истории он не ведёт.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {busy && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
          <Switch checked={data.enabled} disabled={!canEdit || busy || !ready} onCheckedChange={(v) => void toggle(v)} />
        </div>
      </div>
      {!ready && (
        <p className="text-xs text-muted-foreground">
          {(data.router_model || '').toLowerCase() !== 'keenetic'
            ? 'Пока умеем собирать только с Keenetic.'
            : 'Заполните адрес, логин и пароль роутера — без них опрашивать нечего.'}
        </p>
      )}
      {probe && <p className="text-xs text-muted-foreground">{probe}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
      {data.enabled && (
        <Link to="/management/analytics?tab=wifi" className="inline-block text-xs text-primary hover:underline">
          Смотреть в статистике{data.sessions > 0 ? ` (${data.sessions} записей)` : ''}
        </Link>
      )}

      {data.enabled && <WifiNetworksRow zoneId={zoneId} canEdit={canEdit} counted={data.networks ?? []} onSaved={() => void mutate()} />}

      <CaptivePortalRow zoneId={zoneId} canEdit={canEdit} />
    </section>
  );
}


/** Сети роутера: SSID, открытая ли, чей каптив-портал — и какие из них считаем
 *  в статистике (Алексей 17.09, AI29/AI31). Список спрашиваем у роутера вживую. */
function WifiNetworksRow({ zoneId, canEdit, counted, onSaved }: {
  zoneId: number | string; canEdit: boolean; counted: string[]; onSaved: () => void;
}) {
  const { data } = useSWR(`zone-wifi-networks:${zoneId}`, () => getWifiNetworks(zoneId).then(r => r.data), {
    revalidateOnFocus: false, shouldRetryOnError: false,
  });
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  if (data.error) return <p className="text-xs text-muted-foreground">Сети роутера не прочитались: {data.error}</p>;

  async function pick(key: string) {
    // Пусто = считаем все; выбор одной сети — это и есть «считать только её».
    const next = counted.includes(key) ? counted.filter((k) => k !== key) : [...counted, key];
    setBusy(true);
    try {
      await setWifiNetworks(zoneId, next);
      onSaved();
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-3 space-y-1.5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        Сети роутера {busy && <Loader2 className="size-3 animate-spin" />}
      </div>
      <ul className="space-y-1">
        {data.networks.map((n) => (
          <NetworkRow key={n.interface} n={n} counted={counted.length === 0 || counted.includes(n.key)}
            explicit={counted.includes(n.key)} canEdit={canEdit} onPick={() => void pick(n.key)} />
        ))}
      </ul>
      <p className="text-[11px] text-muted-foreground">
        {counted.length === 0 ? 'Считаем все сети. Нажмите на сеть, чтобы считать только её.' : 'Считаем только отмеченные.'}
      </p>
    </div>
  );
}

function NetworkRow({ n, counted, explicit, canEdit, onPick }: {
  n: WifiNetworkDto; counted: boolean; explicit: boolean; canEdit: boolean; onPick: () => void;
}) {
  const portal = n.captive;
  return (
    <li>
      <button
        type="button"
        disabled={!canEdit}
        onClick={onPick}
        className={cn('flex w-full items-start gap-2 rounded-md border px-2 py-1.5 text-left transition-colors',
          explicit ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/50',
          !counted && 'opacity-60')}
      >
        <Wifi className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 text-sm">
            {n.ssids.length ? n.ssids.join(' · ') : n.title}
            <span className="text-xs text-muted-foreground">{n.guest ? 'гостевая' : 'основная'}</span>
            <span className="text-xs text-muted-foreground">{n.open ? 'без пароля' : 'с паролем'}</span>
            {!n.up && <span className="text-xs text-destructive">не поднята</span>}
          </span>
          {portal && (
            <span className="block text-xs text-muted-foreground">
              {portal.ours
                ? 'Гостевой портал: наш'
                : `Гостевой портал: ${portal.profile || new URL(portal.uam_server || 'https://x').hostname} — не наш. Чтобы поставить наш, сначала уберите его в настройках роутера.`}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

/** Гостевой портал (W3/W4) живёт рядом со статистикой — это тоже «настройка роутера»
 *  с точки зрения управляющего, хотя страницу держим мы, а не роутер. */
function CaptivePortalRow({ zoneId, canEdit }: { zoneId: number | string; canEdit: boolean }) {
  const key = `zone-captive:${zoneId}`;
  const { data, mutate } = useSWR<CaptiveSettingsDto>(key, () => getCaptiveSettings(zoneId).then(r => r.data), {
    revalidateOnFocus: false, shouldRetryOnError: false,
  });
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  if (!data) return null;

  async function create() {
    setCreating(true);
    try {
      await putCaptivePortal(zoneId, { identity: 'linza', is_active: false, texts: {} });
      await mutate();
      setOpen(true);
    } finally { setCreating(false); }
  }

  return (
    <div className="mt-3 border-t border-dashed border-border pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium">Гостевой портал</div>
          <p className="text-xs text-muted-foreground">
            Страница входа в Wi-Fi: телефон, код, ваши спецпредложения. Номер сохраняем — этого требует закон о публичных сетях.
          </p>
        </div>
        {canEdit && (data.portal
          ? <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Настроить портал</Button>
          : <Button size="sm" variant="outline" onClick={() => void create()} disabled={creating}>
              {creating && <Loader2 className="size-3.5 animate-spin" />} Создать портал
            </Button>)}
      </div>
      {open && data.portal && (
        <CaptivePortalEditor zoneId={zoneId} settings={data} onClose={() => setOpen(false)} onSaved={async () => { await mutate(); }} />
      )}
    </div>
  );
}
